"use client";

import { useEffect, useSyncExternalStore } from "react";
import Link from "next/link";
import { clearLastReading, getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";

const CONTINUE_CONSULTATION_CARD_MIN_HEIGHT = 144;

function trackEventLazy(name: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  void import("@/lib/analytics-events").then(({ trackEvent }) => trackEvent(name, properties));
}

/**
 * Returning-visitor resume affordance for chat entry surfaces.
 *
 * Every generated kundali is persisted and resumable at /chat/<id>, but entry
 * pages used to greet returning seekers with an empty birth form. This card
 * reads the last reading from the lib/last-reading external store and leads
 * with "continue your consultation" — a chat-continuation element, so it
 * belongs in the first viewport of a chat-intent page (rule 61). Some pages
 * reserve the same slot for first-time visitors so a stored reading discovered
 * during hydration cannot push the birth form and register as CLS.
 */
export default function ContinueConsultationCard({
  reserveEmpty = true,
}: {
  reserveEmpty?: boolean;
}) {
  const reading = useSyncExternalStore(subscribeLastReading, getLastReadingSnapshot, () => null);

  useEffect(() => {
    if (reading) {
      trackEventLazy("chat_resume_card_shown", {
        reading_id: reading.id,
        language: reading.language,
      });
    }
    // Fire once per shown reading id, not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reading?.id]);

  if (!reading) {
    if (!reserveEmpty) return null;
    return (
      <div
        aria-hidden="true"
        data-testid="continue-consultation-card-slot"
        className="mb-4"
        style={{ minHeight: CONTINUE_CONSULTATION_CARD_MIN_HEIGHT }}
      />
    );
  }
  const href = `/chat/${encodeURIComponent(reading.id)}${reading.language === "hi" ? "?lang=hi" : ""}`;

  return (
    <div
      data-testid="continue-consultation-card"
      className="mb-4 rounded-xl border p-4"
      style={{ borderColor: "var(--ish-accent)", background: "var(--ish-accent-light, #fdf1e7)" }}
    >
      <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-[var(--ish-accent)]">
        {reading.name ? `${reading.name}, welcome back` : "Welcome back"}
      </p>
      <p className="mt-1 text-[14px] leading-relaxed text-[var(--ish-text-muted)]">
        Your kundali is already prepared — continue the consultation from where you left off. No
        need to re-enter your birth details.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Link
          href={href}
          onClick={() =>
            trackEventLazy("chat_resume_clicked", {
              reading_id: reading.id,
              language: reading.language,
            })
          }
          className="inline-flex rounded-lg px-5 py-2.5 text-[14px] font-semibold text-white"
          style={{ background: "var(--ish-accent)" }}
        >
          Continue consultation
        </Link>
        <button
          type="button"
          onClick={() => {
            trackEventLazy("chat_resume_dismissed", { reading_id: reading.id });
            clearLastReading();
          }}
          className="text-[13px] font-semibold underline text-[var(--ish-text-muted)]"
        >
          Start a new kundali
        </button>
      </div>
    </div>
  );
}
